import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaHeadset,
  FaQuestionCircle,
} from "react-icons/fa";
import { COMPANY_DATA } from "../../constant/CompanyData";
import NetworkOverlay from "../HeroSection/NetworkOverlay";

const HELP_OPTIONS = [
  {
    icon: FaPhoneAlt,
    title: "Talk to Sales",
    description:
      "Planning a new deployment or migration? Speak with our infrastructure team directly.",
    actionLabel: COMPANY_DATA.phoneNumber,
    href: `tel:${COMPANY_DATA.phoneNumber}`,
  },
  {
    icon: FaEnvelope,
    title: "Email Us",
    description:
      "Send project details, requirements, or questions — we typically reply within 2 business hours.",
    actionLabel: COMPANY_DATA.mailID,
    href: COMPANY_DATA.mailURL,
  },
  {
    icon: FaHeadset,
    title: "24/7 Technical Support",
    description:
      "Existing client with an urgent issue? Our NOC team is available around the clock.",
    actionLabel: "Call support",
    href: `tel:${COMPANY_DATA.phoneNumber}`,
  },
  {
    icon: FaQuestionCircle,
    title: "Browse the FAQ",
    description:
      "Common questions about colocation, pricing, and uptime — answered instantly.",
    actionLabel: "View FAQ",
    href: "/resources/faq",
    internal: true,
  },
];

const HelpCard = ({ option }) => {
  const Icon = option.icon;

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useSpring(
    useTransform(y, [-0.5, 0.5], [8, -8]),
    {
      stiffness: 250,
      damping: 20,
    }
  );

  const rotateY = useSpring(
    useTransform(x, [-0.5, 0.5], [-8, 8]),
    {
      stiffness: 250,
      damping: 20,
    }
  );

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();

    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  const CardWrapper = option.internal ? Link : "a";

  const wrapperProps = option.internal
    ? { to: option.href }
    : { href: option.href };

  return (
    <motion.div
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      whileHover={{ y: -6 }}
      style={{
        rotateX,
        rotateY,
        transformPerspective: 800,
      }}
      className="h-full"
    >
      <CardWrapper
        {...wrapperProps}
        className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/5 p-6 text-left backdrop-blur-sm transition-colors duration-300 hover:border-[rgba(var(--website-secondary-rgb),0.5)] hover:bg-white/10"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[rgba(var(--website-secondary-rgb),0.15)] text-[var(--website-secondary-color)] transition-colors duration-300 group-hover:bg-[var(--website-secondary-color)] group-hover:text-[var(--website-primary-color)]">
          <Icon size={20} />
        </div>

        <h3 className="mt-4 text-lg font-semibold text-white">
          {option.title}
        </h3>

        <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-300">
          {option.description}
        </p>

        <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--website-secondary-color)] transition group-hover:gap-3">
          {option.actionLabel}
        </span>
      </CardWrapper>
    </motion.div>
  );
};

const ContactHelpOptions = () => {
  return (
    <section className="relative overflow-hidden bg-[var(--surface-dark)] px-6 py-20">
      {/* Network Background */}
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-30"
        aria-hidden="true"
      >
        <NetworkOverlay />
      </div>

      {/* Left Glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 top-0 z-0 h-96 w-96 rounded-full bg-[rgba(var(--website-secondary-rgb),0.1)] blur-3xl animate-[float-slow_14s_ease-in-out_infinite]"
      />

      {/* Right Glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 bottom-0 z-0 h-96 w-96 rounded-full bg-[rgba(var(--website-secondary-rgb),0.1)] blur-3xl animate-[float-slow_18s_ease-in-out_infinite_reverse]"
      />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-6xl text-center">
        <p className="mb-3 text-sm uppercase tracking-[0.24em] text-[var(--website-secondary-color)]">
          How can we help?
        </p>

        <h1 className="mx-auto mt-3 max-w-2xl text-3xl font-bold tracking-tight text-white md:text-4xl">
          Choose the fastest way to reach us
        </h1>

        <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-slate-300">
          Whether you need a quote, urgent support, or a quick answer — pick
          what fits, or fill out the form below.
        </p>

        {/* Help Cards */}
        <div className="mt-12 grid gap-6 [perspective:800px] sm:grid-cols-2 lg:grid-cols-4">
          {HELP_OPTIONS.map((option) => (
            <HelpCard
              key={option.title}
              option={option}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactHelpOptions;